import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import MovieCard from '@/components/base/MovieCard';
import { movies, movieCategories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>(movies);
  const [sortBy, setSortBy] = useState('default');

  const currentCategory = movieCategories.find(c => c.slug === slug) || movieCategories[0];

  useEffect(() => {
    if (slug && slug !== activeCategory) {
      setActiveCategory(slug);
    }
  }, [slug, activeCategory]);

  useEffect(() => {
    let result = [...movies];

    if (activeCategory !== 'all') {
      result = result.filter(m => m.category === activeCategory);
    }

    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result.sort((a, b) => b.year - a.year);
    } else if (sortBy === 'newest') {
      result = result.filter(m => m.isNew).concat(result.filter(m => !m.isNew));
    }

    setFilteredMovies(result);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 桃子影院`,
    description: `桃子影院${currentCategory.name}大全，最新${currentCategory.name}在线观看，高清超清蓝光4K画质，永久免费不缓冲秒开播放。`,
    url: typeof window !== 'undefined' ? window.location.href : '',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory.name,
        item: typeof window !== 'undefined' ? window.location.href : '',
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`桃子影院${currentCategory.name} - ${currentCategory.name}大全在线观看`}
        description={`桃子影院${currentCategory.name}大全，${currentCategory.name}排行榜，最新${currentCategory.name}在线观看。高清超清蓝光4K画质，永久免费不缓冲秒开播放。每日更新最新${currentCategory.name}资源。`}
        keywords={`${currentCategory.name},桃子影院,电影大全,在线观看,免费电影,高清电影`}
        canonicalPath={`/category/${slug}`}
        jsonLd={{ ...jsonLd, ...breadcrumbLd }}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              <strong>{currentCategory.name}</strong>
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              桃子影院为您精选{currentCategory.name}资源，高清超清蓝光4K画质，永久免费在线观看
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="lg:w-48 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="text-sm font-semibold text-foreground-700 mb-3">电影分类</h3>
                <div className="flex flex-wrap lg:flex-col gap-1.5">
                  {movieCategories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        activeCategory === cat.slug
                          ? 'bg-primary-500 text-white'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <p className="text-sm text-foreground-500">
                  共 <span className="font-semibold text-foreground-900">{filteredMovies.length}</span> 部电影
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                  {[
                    { value: 'default', label: '默认' },
                    { value: 'rating', label: '评分最高' },
                    { value: 'year', label: '最新年份' },
                    { value: 'newest', label: '最新上映' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={`px-3 py-1.5 rounded-md text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        sortBy === option.value
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-movie-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无电影资源</p>
                  <p className="text-foreground-400 text-xs mt-1">我们正在努力更新，请稍后再来</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}